import { Grid, SxProps, Theme, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  GridSortModel,
  GridCellParams,
  GridColDef,
  GridSortItem,
  GridRowHeightParams,
  GridRowSelectionModel,
  esES,
} from "@mui/x-data-grid";
import isEqual from "lodash/isEqual";
import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import TableHeader from "../../containers/TableHeader/TableHeader";
import SearchTableMessage from "../SearchTableMessage/SearchTableMessage";
import AddEditItem from "./AddEditItem/AddEditItem";
import { CopyItem } from "./CopyItem/CopyItem";
import EditDeletePopover from "./EditDeletePopover/EditDeletePopover";
import { TableProps, State } from "./interfaces";
import RecoverItem from "./RecoverItem/RecoverItem";
import RemoveItem from "./RemoveItem/RemoveItem";

import RemoveItemContainer from "./RemoveItemContainer/RemoveItemContainer";
import {
  Actions,
  // AddButtonLink,
  // EditButtonLink,
  // ReadonlyIconContainer,
} from "./styles";
import { addStylesForHeaderPinnedColumns, addStylesForPinnedColumns, rowsPerPageOptionsValues } from "./utils";
import { SPACING } from "../../_globalStore/redux/reducers/Theme/themeConstants";
import { PlusIcon } from "../../assets/icons";
import useSearchOptions from "../../custom-hooks/useSearchOptions/useSearchOptions";
import useWindowSize from "../../custom-hooks/useWindowSize/useWindowSize";
import { Page } from "../../models/api/page";
import { PropsFilter } from "../../models/api/props-filter";
import { ListSortDirection } from "../../models/api/sort";
import { DTOModel } from "../../models/base/dto-model";
import { RefModel } from "../../models/base/ref-model";
import { SearchOptions } from "../../models/search-options/search-options";

import { toastifyResponse } from "../../utils/toastify-response/toastify-response";

import AddEditButton from "../AddEditButton/AddEditButton";
import { StyledIconButton } from "../AddEditButton/styles";
import LocalizedDataGrid from "../LocalizedDataGrid/LocalizedDataGrid";

const Table = ({
  apiRef,
  translationRootPath,
  displayTitle = true,
  tableHeight,
  tableHeightAdjustment = 0,
  defaultPageSize = 10,
  rowsPerPageOptions = rowsPerPageOptionsValues,
  rowHeight = 72,
  columns,
  serviceApi,
  customSaveServiceApi,
  loadItemsPath,
  saveItemsPath,
  updateGlobalStateCallback,
  propsFilter,
  readOnly = false,
  allowEdit,
  formFields,
  verticalOrientation,
  defaultItem,
  filter,
  quickFilters,
  addEditType = "form",
  addEditPageRootPath,
  addEditPageBaseProp,
  CustomAddEditComponent,
  CustomButtonToRender,
  CustomRemoveComponent,
  changeInsteadDelete,
  shouldReload,
  notifyParent,
  editDepth,
  depthSelection,
  propsForCustomAddEdit,
  propsForCustomButtonToRender,
  propsForCustomRemoveComponent,
  pinnedColumns,
  getRowIdByItemIndex,
  dynamicColumns = [],
  hideRemoveIcon = false,
  checkIsRowDeletable,
  showNoItems,
  checkedRows, //used with ref object
  handleCheckedRows, //used with state object
  checkIsRowEditable,
  getRowHeight,
  getRowClassNameFromSx,
  sxProp,
  disableCustomLogic,
  charts,
  hideSelectionColumn = false,
  AdditionalActionsforAddEditColumn,
  propsForAdditionalActionsforAddEditColumn,
  copyItem,
  propsForCopyItemActions,
  hideTableHeaderButton = false,
  enableSelection, //This is useful to enable the selction column if we have readOnly if for instance we need to return selected Items to parent
  maxRowsToSelect,
  getAllItems,
  overrideNoItemsMessage,
  allowRecoverItems,
  checkIsRowRecoverable,
  refreshData,
  allowClone = false,
  getSearchQuery,
  hideSearch = false,
}: TableProps) => {
  const { t, i18n } = useTranslation();
  const tRP = "components.reusable_table";
  const { height } = useWindowSize();
  const { searchOptions, handleChangeSearchOptions } = useSearchOptions(filter);
  const theme = useTheme();
  const tableRef = useRef<HTMLDivElement | null>(null);

  const [state, setState] = useState<State>({
    items: [],
    totalItems: 0,
    loading: false,
    error: "",
    defaultItem: null,
    page: {
      pageNumber: 0,
      pageSize: defaultPageSize,
    },
    selectedItems: [],
    deleteMessageOpen: false,
    deleteMessageText: "",
    tableHeight,
    editColumnId: uuidv4(), // is need to refresh edit column after reload
  });

  const [newStyles, setNewStyles] = useState<any>({});
  const [newHeaderStyles, setNewHeaderStyles] = useState<any>({});
  const [allCellsWidth, setAllCellsWidth] = useState<number[]>([]);
  const [tableWidth, setTableWidth] = useState<number>(0);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>(
    state.selectedItems.map((el) => el.id)
  );
  const dataGridRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (defaultItem) setState((state) => ({ ...state, defaultItem }));
  }, [defaultItem]);

  const updateGlobalState = useCallback(
    () => updateGlobalStateCallback && updateGlobalStateCallback(),
    [updateGlobalStateCallback]
  );

  //#region initialized serviceApi
  const serviceApiInstance = useMemo(() => {
    const serviceApiObj = serviceApi;

    if (loadItemsPath) serviceApiObj.useLoadItemsPath(loadItemsPath);
    if (saveItemsPath) serviceApiObj.useSaveItemsPath(saveItemsPath);

    return serviceApiObj;
  }, [serviceApi, loadItemsPath, saveItemsPath]);
  // #endregion

  // #region sort model
  const [sortModel, setSortModel] = useState<GridSortItem[]>([
    {
      field: serviceApiInstance ? serviceApiInstance.defaultSort[0].name : "",
      sort: serviceApiInstance ? (serviceApiInstance.defaultSort[0].direction === 0 ? "asc" : "desc") : "asc",
    },
  ]);

  const handleChangeSortModel = (model: GridSortModel) => {
    if (!isEqual(sortModel, model) && model.length) setSortModel(model);
  };
  // #endregion

  // #region load data
  const loadData = useCallback(
    async (page: Page, searchOptions: SearchOptions, sortModel: GridSortItem[], signal?: AbortSignal) => {
      const { field, sort } = sortModel[0];
      const { Ascending, Descending } = ListSortDirection;

      const response = await serviceApiInstance.loadItems(
        {
          isDeleted: searchOptions.deletedItems,
          ...(propsFilter ?? {}),
        } as PropsFilter,
        searchOptions.query,
        sortModel.length === 2
          ? [
              {
                name: field,
                direction: sort === "asc" ? Ascending : Descending,
              },
              {
                name: sortModel[1]?.field,
                direction: sort === "asc" ? Ascending : Descending,
              },
            ]
          : [
              {
                name: field,
                direction: sort === "asc" ? Ascending : Descending,
              },
            ],
        page,
        searchOptions.exactMatch,
        60,
        signal
      );

      if (!signal?.aborted) {
        toastifyResponse(response, "failed", undefined, "failure_fetching_data", "error_fetching_data");
      }
      return response;
    },
    [serviceApiInstance, propsFilter]
  );
  // #endregion

  // #region load data callback
  const loadDataCallback = useCallback(
    async (signal?: AbortSignal, doNotRefreshEditColumn?: boolean): Promise<void> => {
      if (showNoItems) {
        setState((state) => ({
          ...state,
          items: [],
          totalItems: 0,
          loading: false,
          error: "",
          defaultItem: null,
          page: {
            pageNumber: 0,
            pageSize: defaultPageSize,
          },
        }));
        return;
      }

      setState((state) => ({
        ...state,
        loading: true,
        ...(!doNotRefreshEditColumn && {
          editColumnId: uuidv4(),
        }),
      }));

      let finalSortModel: any = null;

      if (sortModel[0].field.split(",").length === 2) {
        const firstField = sortModel[0].field.split(",")[0];
        const secondField = sortModel[0].field.split(",")[1];
        finalSortModel = [
          { field: firstField, sort: sortModel[0].sort },
          { field: secondField, sort: sortModel[0].sort },
        ];
      }

      try {
        const { items, totalItems, error } = await loadData(
          {
            pageNumber: state.page.pageNumber,
            pageSize: state.page.pageSize,
          },
          searchOptions,
          finalSortModel ?? sortModel,
          signal
        );

        if (!signal || !signal.aborted) {
          setState((state) => ({
            ...state,
            error,
            items,
            totalItems,
          }));

          getAllItems && getAllItems(items);
        }
      } catch (error: any) {
        if (!signal || !signal.aborted)
          setState((state) => ({
            ...state,
            error: error?.message ?? error,
            totalItems: 0,
            items: [],
            selectedItems: [],
          }));

        getAllItems && getAllItems([]);
      } finally {
        if (!signal || !signal.aborted) setState((state) => ({ ...state, loading: false }));
      }
    },
    [defaultPageSize, showNoItems, loadData, state.page.pageNumber, state.page.pageSize, searchOptions, sortModel]
  );
  // #endregion

  // const getItemById = useCallback((id): RefModel =>{
  //   return state.items.filter((item) => item.id === id)[0]
  //  },[state.items])

  useEffect(() => {
    if (serviceApiInstance) {
      let timer: ReturnType<typeof setTimeout>;
      const abortController = new AbortController();

      searchOptions.query.length === 0
        ? loadDataCallback(abortController.signal)
        : (timer = setTimeout(
            () => loadDataCallback(abortController.signal),
            searchOptions.query.length === 0 ? 0 : 1000
          ));

      return () => {
        clearTimeout(timer);
        abortController.abort();
      };
    }
  }, [serviceApiInstance, loadDataCallback, searchOptions.query, searchOptions.exactMatch]);

  useEffect(() => {
    if (shouldReload) {
      let timer: ReturnType<typeof setTimeout>;
      const abortController = new AbortController();

      searchOptions.query.length === 0
        ? loadDataCallback(abortController.signal)
        : (timer = setTimeout(
            () => loadDataCallback(abortController.signal),
            searchOptions.query.length === 0 ? 0 : 1000
          ));

      notifyParent && notifyParent(false);

      return () => {
        clearTimeout(timer);
        abortController.abort();
      };
    }
  }, [shouldReload, loadDataCallback, notifyParent, searchOptions.query.length]);

  // #region reset default
  const handleResetDefaultItem = () => {
    if (defaultItem) setState((state) => ({ ...state, defaultItem }));
  };
  // #endregion

  // #region handle cancel
  const handleCancel = (resetItem: RefModel, effect: "add" | "edit") => {
    if (effect === "add") handleResetDefaultItem();

    if (effect === "edit") {
      setState((state) => ({
        ...state,
        items: state.items.map((item: RefModel) => (item.id === resetItem.id ? resetItem : item)),
      }));

      getAllItems && getAllItems(state.items.map((item: RefModel) => (item.id === resetItem.id ? resetItem : item)));
    }
  };
  // #endregion

  // #region repetitive Props For Edit Component
  const repetitivePropsForEditComponent = {
    titleForEditComponent: t(`${translationRootPath}.title_for_edit_component`),
    formFields,
    verticalOrientation,
    handleCancel,
    allowEdit,
    serviceApiInstance: customSaveServiceApi ?? serviceApiInstance,
    updateGlobalState,
    handleReload: loadDataCallback,
  };
  // #endregion

  // #region constant in items
  const constantInItems = useCallback((): boolean => {
    if (editDepth) {
      return (
        state.selectedItems.filter(
          (_item: any) => _item.isConstant || !_item[editDepth] || (_item[editDepth] as any).isConstant
        ).length > 0
      );
    }
    return state.selectedItems.filter((_item) => _item.isConstant).length > 0;
  }, [editDepth, state.selectedItems]);
  // #endregion

  // #region check If Selected Items Deletable
  const checkIfSelectedItemsDeletable = useCallback(() => {
    if (checkIsRowDeletable) {
      if (editDepth) {
        return (
          state.selectedItems.filter((item) => !checkIsRowDeletable(item[editDepth as keyof RefModel])).length === 0
        );
      } else {
        return state.selectedItems.filter((item) => !checkIsRowDeletable(item)).length === 0;
      }
    }
  }, [editDepth, checkIsRowDeletable, state.selectedItems]);
  // #endregion

  useEffect(() => {
    state.selectedItems.length && !enableSelection
      ? readOnly || hideRemoveIcon
        ? setState((state) => ({
            ...state,
            deleteMessageOpen: true,
            deleteMessageText: t(`${tRP}.delete_messages.not_allowed`),
          }))
        : state.selectedItems.length < 4
        ? constantInItems()
          ? setState((state) => ({
              ...state,
              deleteMessageOpen: true,
              deleteMessageText: t(`${tRP}.delete_messages.not_allowed_constant`),
            }))
          : checkIsRowDeletable && !checkIfSelectedItemsDeletable()
          ? setState((state) => ({
              ...state,
              deleteMessageOpen: true,
              deleteMessageText: t(`${tRP}.delete_messages.not_allowed_item_not_deletable`),
            }))
          : setState((state) => ({
              ...state,
              deleteMessageOpen: true,
              deleteMessageText: `${state.selectedItems.length} ${t(`${tRP}.delete_messages.items_selected`)}`,
            }))
        : setState((state) => ({
            ...state,
            deleteMessageOpen: true,
            deleteMessageText: t(`${tRP}.delete_messages.not_allowed_more_than_three_items`),
          }))
      : setState((state) => ({
          ...state,
          deleteMessageOpen: false,
          deleteMessageText: "",
        }));
  }, [
    // checkedRows,
    // _checkedRows,
    readOnly,
    hideRemoveIcon,
    checkIfSelectedItemsDeletable,
    checkIsRowDeletable,
    constantInItems,
    state.selectedItems,
    enableSelection,
  ]);

  useEffect(() => {
    if (checkedRows) checkedRows.current = state.selectedItems;
    if (handleCheckedRows) handleCheckedRows(state.selectedItems);
  }, [checkedRows, state.selectedItems]);

  // #region edit columns
  const editColumn: GridColDef = useMemo(() => {
    return {
      field: "actions",
      headerName: "",
      headerAlign: "center",
      disableColumnMenu: true,
      sortable: false,
      width: 50,
      align: "center",
      renderCell: ({ row }: GridCellParams) => {
        return (
          <Actions>
            <EditDeletePopover
              key={state.editColumnId}
              EditElement={
                row.isReadonly ? (
                  <Typography style={{ color: theme.palette.text.disabled, marginLeft: "8px" }}>Edit </Typography>
                ) : (
                  <>
                    {!!checkIsRowEditable && !checkIsRowEditable(row) ? (
                      <Typography style={{ color: theme.palette.text.disabled, marginLeft: "8px" }}>Edit </Typography>
                    ) : (
                      <>
                        {(addEditType === "form" || addEditType === "formAndCustom") && (
                          <AddEditItem
                            effect="edit"
                            item={row as DTOModel}
                            handleNotifyParent={() => {
                              notifyParent && notifyParent(true);
                            }}
                            {...repetitivePropsForEditComponent}
                            buttonText={`${t("buttons.edit")}`}
                          >
                            {/* <FontAwesomeIcon icon={faPencilAlt} /> */}
                          </AddEditItem>
                        )}
                        {((allowClone && addEditType === "form") || addEditType === "formAndCustom") && (
                          <AddEditItem
                            effect="edit"
                            item={
                              {
                                ...row,
                                id: uuidv4(),
                              } as DTOModel
                            }
                            handleNotifyParent={() => {
                              notifyParent && notifyParent(true);
                            }}
                            {...repetitivePropsForEditComponent}
                            buttonText={`${t("buttons.clone")}`}
                          >
                            {/* <FontAwesomeIcon icon={faPencilAlt} /> */}
                          </AddEditItem>
                        )}
                        {addEditType === "page" && (
                          <>
                            <Link
                              to={`${addEditPageRootPath}/${row.id}?new=false${
                                addEditPageBaseProp && row[addEditPageBaseProp]
                                  ? `&basePropId=${row[addEditPageBaseProp]?.id}`
                                  : ""
                              }`}
                              style={{ textDecoration: "none" }}
                            >
                              <AddEditButton
                                title={`${t("buttons.edit")}`}
                                effect={"edit"}
                                buttonText={`${t("buttons.edit")}`}
                              />
                              {/* <StyledIconButton
                        asLink={false}
                        isMini={false}
                        effect={"edit"}
                        title={"Edit"}
                      >
                        <FontAwesomeIcon icon={faPencilAlt} />
                      </StyledIconButton> */}
                            </Link>
                          </>
                        )}
                        {(addEditType === "custom" || addEditType === "formAndCustom") && !!CustomAddEditComponent ? (
                          <>
                            {propsForCustomAddEdit?.editModes ? (
                              propsForCustomAddEdit.editModes.map((editMode: string) => (
                                <CustomAddEditComponent
                                  key={editMode}
                                  effect="edit"
                                  item={editDepth ? row[editDepth] : row}
                                  handleNotifyParent={() => {
                                    updateGlobalStateCallback && updateGlobalStateCallback();
                                    loadDataCallback();
                                  }}
                                  higherItem={row} //this is useful when using edit depth
                                  buttonVariant={"text"}
                                  {...propsForCustomAddEdit}
                                  editMode={editMode}
                                />
                              ))
                            ) : (
                              <CustomAddEditComponent
                                effect="edit"
                                item={editDepth ? row[editDepth] : row}
                                handleNotifyParent={() => {
                                  updateGlobalStateCallback && updateGlobalStateCallback();
                                  loadDataCallback();
                                }}
                                higherItem={row} //this is useful when using edit depth
                                buttonVariant={"text"}
                                {...propsForCustomAddEdit}
                              />
                            )}
                          </>
                        ) : null}
                      </>
                    )}
                    {AdditionalActionsforAddEditColumn && (
                      <AdditionalActionsforAddEditColumn
                        item={row}
                        handleNotify={loadDataCallback}
                        {...propsForAdditionalActionsforAddEditColumn}
                      />
                    )}
                    {copyItem && (
                      <CopyItem
                        item={row}
                        handleNotify={notifyParent}
                        setLoading={(loading: boolean) =>
                          setState((state) => ({
                            ...state,
                            loading,
                          }))
                        }
                        {...propsForCopyItemActions}
                      />
                    )}
                    {allowRecoverItems && row.isDeleted && (
                      <RecoverItem
                        items={[row] as DTOModel[]}
                        serviceApiInstance={customSaveServiceApi ?? serviceApiInstance}
                        handleReload={loadDataCallback}
                        updateGlobalStateCallback={updateGlobalStateCallback}
                        notifyParent={handleUnselectAfterDelete}
                        buttonText
                      />
                    )}
                    {/* {(!editDepth || (editDepth && !!row[editDepth]?.id)) &&
                  (hideRemoveIcon ||
                  (!!checkIsRowDeletable && !checkIsRowDeletable(row)) ||
                  row.isConstant ? (
                    <Typography style={{ color: theme.palette.text.disabled }}>
                      Delete{" "}
                    </Typography>
                  ) : (
                    !hideRemoveIcon &&
                    (CustomRemoveComponent ? (
                      <CustomRemoveComponent
                        item={row as DTOModel}
                        serviceApiInstance={
                          customSaveServiceApi ?? serviceApiInstance
                        }
                        itemID={
                          editDepth && !!row[editDepth]
                            ? row[editDepth].id
                            : row.id
                        }
                        handleReload={loadDataCallback}
                        updateGlobalStateCallback={updateGlobalStateCallback}
                        notifyParent={handleUnselectAfterDelete}
                        {...propsForCustomRemoveComponent}
                      />
                    ) : (
                      <RemoveItem
                        items={
                          editDepth && !!row[editDepth]
                            ? [
                                {
                                  ...row[editDepth],
                                  ...row,
                                  id: row[editDepth].id,
                                },
                              ]
                            : [row as DTOModel]
                        }
                        itemName={row["name"] ?? row["description"]}
                        serviceApiInstance={
                          customSaveServiceApi ?? serviceApiInstance
                        }
                        handleReload={loadDataCallback}
                        changeInsteadDelete={changeInsteadDelete}
                        updateGlobalStateCallback={updateGlobalStateCallback}
                        notifyParent={handleUnselectAfterDelete}
                        buttonText={true}
                        disableCustomLogic={disableCustomLogic}
                      />
                    ))
                  ))} */}
                  </>
                )
              }
              DeleteElement={
                !editDepth || (editDepth && !!row[editDepth]?.id) ? (
                  hideRemoveIcon || (!!checkIsRowDeletable && !checkIsRowDeletable(row)) || row.isConstant ? (
                    <Typography style={{ color: theme.palette.text.disabled, marginLeft: "8px" }}>
                      {" "}
                      {`${t("buttons.delete")}`}
                    </Typography>
                  ) : !hideRemoveIcon && !row.isDeleted ? (
                    CustomRemoveComponent ? (
                      <CustomRemoveComponent
                        item={row as DTOModel}
                        serviceApiInstance={customSaveServiceApi ?? serviceApiInstance}
                        itemID={editDepth && !!row[editDepth] ? row[editDepth].id : row.id}
                        handleReload={loadDataCallback}
                        updateGlobalStateCallback={updateGlobalStateCallback}
                        notifyParent={handleUnselectAfterDelete}
                        {...propsForCustomRemoveComponent}
                      />
                    ) : (
                      <RemoveItem
                        items={
                          editDepth && !!row[editDepth]
                            ? [
                                {
                                  ...row[editDepth],
                                  ...row,
                                  id: row[editDepth].id,
                                },
                              ]
                            : [row as DTOModel]
                        }
                        itemName={row["name"] ?? row["description"]}
                        serviceApiInstance={customSaveServiceApi ?? serviceApiInstance}
                        handleReload={loadDataCallback}
                        changeInsteadDelete={changeInsteadDelete}
                        updateGlobalStateCallback={updateGlobalStateCallback}
                        notifyParent={handleUnselectAfterDelete}
                        buttonText={true}
                        disableCustomLogic={disableCustomLogic}
                      />
                    )
                  ) : (
                    <></>
                  )
                ) : (
                  <></>
                )
              }
            />
          </Actions>
        );
      },
    };
  }, [state.editColumnId]);
  // #endregion

  // #region handle Unselect After Delete
  const handleUnselectAfterDelete = useCallback(
    (ids: string[]) => {
      setState((state) => ({
        ...state,
        selectedItems: state.selectedItems.filter((selectedItem) => !ids.includes(selectedItem.id)),
      }));

      notifyParent && notifyParent(true);
    },
    [notifyParent]
  );
  // #endregion

  // #region handle Notify On Chart Resize
  const handleNotifyOnChartResize = useCallback(
    (chartsHeight: number) => {
      if (tableHeight) {
        setState((state) => ({
          ...state,
          tableHeight: (tableHeight as number) + chartsHeight,
        }));
      } else if (tableRef.current?.clientHeight) {
        setState((state) => ({
          ...state,
          tableHeight: (tableRef.current?.clientHeight as number) + chartsHeight,
        }));
      }
    },
    [tableHeight]
  );
  // #endregion

  // #region filteredPinnedColumns
  const filteredPinnedColumns: { left?: string[]; right?: string[] } = useMemo(() => {
    if (!pinnedColumns) return {};

    const result: { left?: string[]; right?: string[] } = {};

    const allColumns = [...columns, ...dynamicColumns, ...(!readOnly || allowEdit ? [editColumn] : [])];

    if (pinnedColumns.left) {
      const leftPinnedColumns = pinnedColumns.left.filter((col) => {
        return !!allColumns.find((origCol) => origCol.field === col);
      });

      result["left"] = leftPinnedColumns;
    }

    if (pinnedColumns.right) {
      const rightPinnedColumns = pinnedColumns.right.filter((col) => {
        return !!allColumns.find((origCol) => origCol.field === col);
      });

      result["right"] = rightPinnedColumns;
    }

    return result;
  }, [pinnedColumns]);

  // #region resultColumns
  const resultColumns = useMemo(() => {
    const allColumns = [...columns, ...dynamicColumns];

    if ((filteredPinnedColumns.left?.length ?? 0) + (filteredPinnedColumns.right?.length ?? 0) > 0) {
      const leftPinnedColumns: GridColDef[] = [];
      const rightPinnedColumns: GridColDef[] = [];

      if (filteredPinnedColumns?.left) {
        filteredPinnedColumns.left.forEach((col) => {
          const originColumn = allColumns.find((origCol) => origCol.field === col);

          originColumn && leftPinnedColumns.push(originColumn);
        });
      }

      if (filteredPinnedColumns?.right) {
        filteredPinnedColumns.right.forEach((col) => {
          const originColumn = allColumns.find((origCol) => origCol.field === col);

          originColumn && rightPinnedColumns.push(originColumn);
        });
      }

      const allPinnedColumns = [...(filteredPinnedColumns?.left ?? []), ...(filteredPinnedColumns?.right ?? [])];
      const filteredColumns = allColumns.filter((origCol) => !allPinnedColumns.includes(origCol.field));

      return [
        ...leftPinnedColumns,
        ...filteredColumns,
        ...rightPinnedColumns,
        ...(!readOnly || allowEdit ? [editColumn] : []),
      ];
    } else {
      return [...allColumns, ...(!readOnly || allowEdit ? [editColumn] : [])];
    }
  }, [columns, dynamicColumns, readOnly, allowEdit, editColumn, filteredPinnedColumns]);
  // #endregion

  useEffect(() => {
    if ((filteredPinnedColumns.left?.length ?? 0) + (filteredPinnedColumns.right?.length ?? 0) > 0) {
      const waitForRender = () => {
        // check when DataGrid is rendered
        const dataGridHeaderRow = document.querySelector(".MuiDataGrid-columnHeadersInner >div");

        if (!dataGridHeaderRow) {
          requestAnimationFrame(waitForRender);
        } else {
          const checkboxSelectionTurnedOn = enableSelection || (!readOnly && !hideSelectionColumn);

          const cellsWidth: number[] = [];

          const headerCells = dataGridHeaderRow.children;

          for (let i = 0; i < headerCells.length; i++) {
            const cell = headerCells[i] as HTMLDivElement;
            const width = cell.offsetWidth;
            cellsWidth.push(width);
          }

          // apply styles for cells that are pinned
          const newStyles = addStylesForPinnedColumns(
            checkboxSelectionTurnedOn,
            cellsWidth,
            filteredPinnedColumns?.left?.length ?? 0,
            filteredPinnedColumns?.right?.length ?? 0,
            !!(!readOnly || allowEdit),
            tableWidth
          );

          setNewStyles(newStyles);

          setAllCellsWidth(cellsWidth);
        }
      };
      waitForRender();
    }
  }, [filteredPinnedColumns, tableWidth]);

  useEffect(() => {
    if (
      (filteredPinnedColumns.left?.length ?? 0) + (filteredPinnedColumns.right?.length ?? 0) > 0 &&
      !!allCellsWidth.length
    ) {
      const waitForRender = () => {
        const columnHeadersInner = document.querySelector(".MuiDataGrid-columnHeadersInner");

        if (!columnHeadersInner) {
          requestAnimationFrame(waitForRender);
        } else {
          const observer = new MutationObserver((mutationsList) => {
            mutationsList.forEach((mutation) => {
              // track when columnHeadersInner changes styles property
              if (mutation.type === "attributes" && mutation.attributeName === "style") {
                const currentTransform = (columnHeadersInner as HTMLDivElement).style.transform;

                const checkboxSelectionTurnedOn = enableSelection || (!readOnly && !hideSelectionColumn);

                // update with actual position pinned header cells
                setNewHeaderStyles(
                  addStylesForHeaderPinnedColumns(
                    checkboxSelectionTurnedOn,
                    allCellsWidth,
                    filteredPinnedColumns?.left?.length ?? 0,
                    filteredPinnedColumns?.right?.length ?? 0,
                    currentTransform,
                    !!(!readOnly || allowEdit),
                    tableWidth
                  )
                );
              }
            });
          });

          // Configure the observer to watch for changes to 'style' attribute
          const config = { attributes: true, attributeFilter: ["style"] };

          // Start observing the DataGrid Header Row Wrapper
          observer.observe(columnHeadersInner, config);
        }
      };
      waitForRender();
    }
  }, [allCellsWidth, filteredPinnedColumns, tableWidth]);

  // #region computed Styles For DataGrid Container
  const computedStylesForDataGridContainer: SxProps<Theme> = useMemo(() => {
    return {
      height: state.tableHeight
        ? `${state.tableHeight + tableHeightAdjustment}px`
        : `calc(
              ${height}px -
              ${
                SPACING[9] * 2 + //main layout paddings
                (document.getElementById("header")?.clientHeight ?? 0) +
                SPACING[7] + //header and its margin
                (document.getElementById("MultitabsHorizontal-Portal")?.clientHeight ?? 0) +
                SPACING[7] + //multitabs and its margin
                (document.getElementById("tableHeader")?.clientHeight ?? 0) +
                SPACING[7] + //table margin
                5 + // extra safety space
                tableHeightAdjustment //add extra height-> negative value , substract height -> positive value
                // 45 + SPACING[7] + //tableHeader and table margin
              }px)`,
      width: "100%",
      position: "relative",
      marginTop: `${SPACING[7]}px`,

      ...newStyles,
      ...newHeaderStyles,
    };
  }, [newStyles, newHeaderStyles, state.tableHeight, tableHeightAdjustment, height, SPACING]);
  // #endregion

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    const resizeObserver = new ResizeObserver((entries) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        for (const entry of entries) {
          !!entry.contentRect.width && setTableWidth(entry.contentRect.width);
        }
      }, 100);
    });

    tableRef.current && resizeObserver.observe(tableRef.current);

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    // set callback to be able to refresh data without rerendering whole component
    refreshData && refreshData((signal?: AbortSignal) => loadDataCallback(signal, true));

    return () => {
      refreshData && refreshData(null);
    };
  }, [refreshData, state.page]);

  return (
    <>
      <TableHeader
        searchOptions={searchOptions}
        handleChangeSearchOptions={handleChangeSearchOptions}
        getSearchQuery={getSearchQuery}
        hideSearch={hideSearch}
        tableTitle={displayTitle ? t(`${translationRootPath}.table_title`) : undefined}
        quickFilters={
          typeof quickFilters === "function"
            ? quickFilters(state.items, state.loading, searchOptions.query)
            : quickFilters
        }
        buttonToRender={
          // <Grid style={{display:"flex"}}>
          // {/* Add button */}
          !readOnly &&
          !hideTableHeaderButton &&
          (CustomButtonToRender ? (
            <CustomButtonToRender
              {...propsForCustomButtonToRender}
              handleNotifyParent={() => {
                updateGlobalStateCallback && updateGlobalStateCallback();
                loadDataCallback();
              }}
            />
          ) : addEditType === "page" ? (
            <Link to={`${addEditPageRootPath}/${uuidv4()}?new=true`}>
              <StyledIconButton
                asLink={false}
                isMini={false}
                effect={"add"}
                title={"Add"}
              >
                <PlusIcon />
              </StyledIconButton>
            </Link>
          ) : addEditType === "custom" ? (
            CustomAddEditComponent && (
              <CustomAddEditComponent
                effect="add"
                buttonVariant="plusButton"
                handleNotifyParent={() => {
                  updateGlobalStateCallback && updateGlobalStateCallback();
                  loadDataCallback();
                }}
                {...propsForCustomAddEdit}
              />
            )
          ) : (
            <AddEditItem
              item={state.defaultItem}
              handleResetDefaultItem={handleResetDefaultItem}
              handleNotifyParent={() => {
                notifyParent && notifyParent(true);
              }}
              buttonVariant="outlined"
              {...repetitivePropsForEditComponent}
            >
              <PlusIcon />
            </AddEditItem>
          ))
        }
        charts={charts}
        handleNotifyOnChartResize={handleNotifyOnChartResize}
      />
      <Grid
        ref={tableRef}
        sx={{
          ...computedStylesForDataGridContainer,
        }}
      >
        {state.totalItems === 0 ? (
          <SearchTableMessage
            loading={state.loading}
            searchOptions={searchOptions}
            error={state.error}
            overrideNoItemsMessage={overrideNoItemsMessage}
          />
        ) : (
          <>
            <LocalizedDataGrid
              apiRef={apiRef}
              ref={dataGridRef}
              sx={{ ...sxProp }}
              style={{ minHeight: "350px" }}
              getRowClassName={getRowClassNameFromSx}
              paginationMode="server"
              paginationModel={{
                page: state.page.pageNumber,
                pageSize: state.page.pageSize,
              }}
              onPaginationModelChange={(model) => {
                setState((state) => ({
                  ...state,
                  page: {
                    pageNumber: model.page,
                    pageSize: model.pageSize,
                  },
                }));
              }}
              pageSizeOptions={rowsPerPageOptions}
              rowHeight={rowHeight}
              rows={state.items}
              rowCount={state.totalItems}
              sortingMode="server"
              sortingOrder={["asc", "desc"]}
              sortModel={sortModel}
              onSortModelChange={handleChangeSortModel}
              loading={state.loading}
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
              error={state.error === "" ? null : state.error}
              // columns={[...columns, ...dynamicColumns, ...(!readOnly || allowEdit ? [editColumn] : [])]}
              columns={resultColumns}
              {...(getRowIdByItemIndex && {
                getRowId: (row) => state.items.findIndex((item) => isEqual(item, row)),
              })}
              {...(depthSelection && {
                getRowId: (row) => row[depthSelection]?.id || row.id,
              })}
              checkboxSelection={enableSelection || (!readOnly && !hideSelectionColumn)}
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={(ids: GridRowSelectionModel) => {
                const selectedIDs = new Set(ids);
                let selectedRows: RefModel[] = [];
                if (editDepth && getRowIdByItemIndex) {
                  // selectedRows = state.items.filter((row) =>
                  //   selectedIDs.has(state.items.indexOf(row))
                  // )
                  // const newCheckedRows = [
                  //   ...state.selectedItems,
                  //   ...selectedRows,
                  // ];
                  // console.log(newCheckedRows);
                  // const fa= Array.from(
                  //             new Set(newCheckedRows.map((a) => a.id))
                  //           ).map((id) => {
                  //               return newCheckedRows.find((a) => a.id === id);
                  //             })
                  //             .filter((el) => el?.id &&  selectedIDs.has(el.id))
                  //             .filter((el)=> !!el) as RefModel[]
                  // setState((state)=>({
                  //   ...state,
                  //   selectedItems: fa
                  // }))
                  // setSelectionModel(fa.map((el, index) => index))
                } else {
                  if (depthSelection) {
                    selectedRows = state.items.filter(
                      (row) => (row as any)[depthSelection] && selectedIDs.has((row as any)[depthSelection].id)
                    );
                  } else {
                    selectedRows = state.items.filter((row) => selectedIDs.has(row.id));
                  }

                  const newCheckedRows = [...state.selectedItems, ...selectedRows];

                  let fa: RefModel[];
                  if (depthSelection) {
                    fa = Array.from(new Set(newCheckedRows.map((a) => (a as any)[depthSelection]?.id)))
                      .map((nestedId) => {
                        return newCheckedRows.find((a) => (a as any)[depthSelection]?.id === nestedId);
                      })
                      .filter(
                        (el) => el && (el as any)[depthSelection]?.id && selectedIDs.has((el as any)[depthSelection].id)
                      )
                      .filter((el) => !!el) as RefModel[];
                  } else {
                    fa = Array.from(new Set(newCheckedRows.map((a) => a.id)))
                      .map((id) => {
                        return newCheckedRows.find((a) => a.id === id);
                      })
                      .filter((el) => el?.id && selectedIDs.has(el.id))
                      .filter((el) => !!el) as RefModel[];
                  }

                  setState((state) => ({
                    ...state,
                    selectedItems: fa,
                  }));

                  if (depthSelection) {
                    setRowSelectionModel(fa.map((el) => (el as any)[depthSelection]?.id || el.id));
                  } else {
                    setRowSelectionModel(fa.map((el) => el.id));
                  }
                }
                // const selectedRows = state.items.filter((row) =>
                //   selectedIDs.has(row.id)
                // );

                // if (state.selectedItems) {
                //   const newCheckedRows = [
                //     ...state.selectedItems,
                //     ...selectedRows,
                //   ];
                //   console.log(newCheckedRows);
                //   const fa= Array.from(
                //               new Set(newCheckedRows.map((a) => a.id))
                //             ).map((id) => {
                //                 return newCheckedRows.find((a) => a.id === id);
                //               })
                //               .filter((el) => el?.id &&  selectedIDs.has(el.id))
                //               .filter((el)=> !!el) as RefModel[]

                //   setState((state)=>({
                //     ...state,
                //     selectedItems: fa
                //   }))

                // // }
                // setSelectionModel(fa.map((el) => el.id));
              }}
              isRowSelectable={({ row }) => {
                // return !!editDepth && row[editDepth] ? !row[editDepth].isDeleted : !row.isDeleted;
                return !!editDepth && row[editDepth]
                  ? !row[editDepth].isDeleted
                  : row.isDeleted
                  ? false
                  : !(
                      maxRowsToSelect &&
                      maxRowsToSelect === state.selectedItems.length &&
                      state.selectedItems.find((el) => el.id !== row.id)
                    );
              }}
              keepNonExistentRowsSelected
              disableRowSelectionOnClick
              {...(getRowHeight && {
                getRowHeight: (params: GridRowHeightParams) => getRowHeight(params.model as DTOModel),
              })}
              {...{
                ...((filteredPinnedColumns.left?.length ?? 0) + (filteredPinnedColumns.right?.length ?? 0) > 0 && {
                  columnBuffer: resultColumns.length + 1,
                }),
              }}
            />

            <RemoveItemContainer
              open={state.deleteMessageOpen}
              onClose={() => {
                setState((state) => ({
                  ...state,
                  deleteMessageOpen: false,
                }));
              }}
              message={state.deleteMessageText}
            >
              {CustomRemoveComponent ? (
                <CustomRemoveComponent
                  items={[...state.selectedItems] as DTOModel[]}
                  serviceApiInstance={customSaveServiceApi ?? serviceApiInstance}
                  {...propsForCustomRemoveComponent}
                  disabled={
                    state.selectedItems.length > 3 ||
                    constantInItems() ||
                    (checkIsRowDeletable && !checkIfSelectedItemsDeletable()) ||
                    readOnly ||
                    hideRemoveIcon
                  }
                  itemIDs={
                    editDepth
                      ? state.selectedItems.map((item: any) => item[editDepth].id)
                      : state.selectedItems.map((item) => item.id)
                  }
                  handleReload={loadDataCallback}
                  updateGlobalStateCallback={updateGlobalStateCallback}
                  notifyParent={handleUnselectAfterDelete}
                />
              ) : (
                <RemoveItem
                  items={
                    editDepth
                      ? state.selectedItems
                          .filter((item: any) => !!item[editDepth])
                          .map((item: any) => ({
                            ...item[editDepth],
                            ...item,
                            id: item[editDepth].id,
                          }))
                      : ([...state.selectedItems] as DTOModel[])
                  }
                  serviceApiInstance={customSaveServiceApi ?? serviceApiInstance}
                  handleReload={loadDataCallback}
                  changeInsteadDelete={changeInsteadDelete}
                  updateGlobalStateCallback={updateGlobalStateCallback}
                  disabled={
                    state.selectedItems.length > 3 ||
                    constantInItems() ||
                    (!!checkIsRowDeletable && !checkIfSelectedItemsDeletable()) ||
                    readOnly ||
                    hideRemoveIcon
                  }
                  notifyParent={handleUnselectAfterDelete}
                />
              )}
            </RemoveItemContainer>
          </>
        )}
      </Grid>
    </>
  );
};

export default memo(Table);
